/**
 * Mediazioni programmate (API: rotte/mediazioni.ts, migrazione 455): forme dei dati e testi comuni
 * alla scheda della persona, alle pagine del mediatore e al cruscotto.
 *
 * Giorno e ora arrivano dall'API gia' nell'ora di Roma (`data` AAAA-MM-GG, `ora` HH:MM): qui si
 * formattano come testo senza passare dal fuso del dispositivo.
 */
import type { TonoEtichetta } from '../componenti/base.tsx'

export type StatoMediazione = 'programmata' | 'svolta' | 'annullata' | 'non_presentata'

/** Riga di GET /persone/:id/mediazioni. */
export type MediazioneScheda = {
  id: string
  data: string
  ora: string
  durata_minuti: number
  lingua: string | null
  lingua_nome: string | null
  sede_id: string | null
  sede_nome: string | null
  luogo: string | null
  nota: string | null
  stato: StatoMediazione
  ore_svolte: number | null
  motivo_annullamento: string | null
  mediatore_id: string
  mediatore_nome: string
  mediatore_cognome: string
  mediatore_usa_gestionale: boolean
  /**
   * Recapito del mediatore (532): dove riceve. Arriva solo a chi programma le mediazioni della persona e
   * solo con la mediazione in programma; altrimenti nullo.
   */
  mediatore_recapito: string | null
  programmata_da_nome: string | null
  programmata_da_cognome: string | null
  /** Percorso su cui finiscono le ore (457); nullo sulle mediazioni programmate prima. */
  percorso_id: string | null
  percorso_tipologia: string | null
  /** Intervento nato dalla mediazione svolta, con il suo stato (bozza finche' non si conferma). */
  intervento_id: string | null
  intervento_stato: string | null
  /** Ente per cui si fa la mediazione (557): lo legge solo chi ha la scheda nel perimetro. */
  ente_id: string | null
  ente_nome: string | null
  ente_sigla: string | null
  /** Mediazioni ripetute (557): la serie e quante sono. */
  serie_id: string | null
  serie_quante: number
  /** Stanza della sede (558). */
  risorsa_id: string | null
  risorsa_nome: string | null
}

/** Percorso della persona su cui possono finire le ore di una mediazione. */
export type PercorsoMediazione = {
  id: string
  tipologia: string
  stato: string
  data_apertura: string
  sede_nome: string | null
  progetto: string | null
  /** Vero se chi guarda ci puo' registrare interventi. */
  utilizzabile: boolean
}

/** GET /persone/:id/mediazioni. */
export type MediazioniPersona = {
  mediazioni: MediazioneScheda[]
  percorsi: PercorsoMediazione[]
  /** Il percorso di mediazione da aprire quando la persona non ne ha uno adatto. */
  percorso_nuovo: { tipologia_codice: string; tipologia: string; sede_id: string | null; puo_aprire: boolean } | null
  puo_programmare: boolean
  puo_registrare_intervento: boolean
}

/** Come e' andata l'email al mediatore (457): `motivo_email` quando non e' partita. */
export type EsitoEmail = { email_inviata: boolean; motivo_email: string | null }

/** Risposta di POST /mediazioni/:id/svolta. */
export type EsitoSvolta = {
  intervento: { id: string; stato: string; percorso: string } | null
  intervento_mancato: { codice: string; messaggio: string } | null
}

/** Riga di GET /mediazioni/mie e testa di GET /mediazioni/mie/:id. */
export type MiaMediazione = {
  id: string
  data: string
  ora: string
  durata_minuti: number
  lingua_nome: string | null
  sede_nome: string | null
  sede_indirizzo: string | null
  /** Stanza della sede (558), se c'e'. */
  stanza?: string | null
  luogo: string | null
  stato: StatoMediazione
  ore_svolte: number | null
  persona: { nome: string; cognome: string }
  completabile: boolean
  puo_segnare_svolta: boolean
  puo_segnare_non_presentata: boolean
  /** Solo nell'elenco: quanti dati mancano. */
  da_completare?: number
}

export type DettaglioMediazione = MiaMediazione & {
  nota: string | null
  programmata_da: { nome: string; cognome: string } | null
  /** Voci della scheda da completare (codici di servizi/mediazioni.ts dell'API). */
  mancanti: string[]
  puo_completare: boolean
}

/** Mediatore dell'albo (GET /mediatori). */
export type Mediatore = {
  id: string; nome: string; cognome: string; interno: boolean; lingue: string[]; attivo: boolean
  utente_id: string | null; usa_gestionale: boolean
  /** Con la lingua cercata: la parla (vero) o non ha lingue nell'albo (falso). */
  parla_lingua?: boolean | null
  /** Mai usato in mediazioni, interventi o servizi: si puo' eliminare (GET /mediatori, 19/10/2026). */
  eliminabile?: boolean
  /** Importato dal vecchio sistema. */
  dal_vecchio_archivio?: boolean
  /** Nomi dei doppioni uniti a questo mediatore (465). */
  altri_nomi?: string[]
  /** Anagrafica e contratto (469): arrivano solo a chi gestisce l'albo. */
  comune_residenza?: string | null
  comune_residenza_nome?: string | null
  comune_residenza_provincia?: string | null
  paese_origine?: string | null
  contratto_tipo?: string | null
  contratto_tipo_altro?: string | null
  contratto_inizio?: string | null
  contratto_fine?: string | null
  contratto_indeterminato?: boolean
  certificato?: boolean | null
  /** La fine del contratto e' passata (469). */
  contratto_finito?: boolean
  /** Email per i contatti di CIDIS e numero dell'albo CIDIS (478): solo a chi gestisce l'albo. */
  email?: string | null
  numero_albo?: string | null
  /** L'utenza collegata non e' ancora entrata (30/10/2026): solo a chi gestisce l'albo. */
  invito_in_attesa?: boolean | null
  /** Sedi CIDIS in cui lavora (477), oppure tutte. */
  sedi?: { id: string; nome: string; attiva: boolean }[]
  tutte_le_sedi?: boolean
  /** Con `prima_sede`: lavora in quella sede (o in tutte), e sta in cima all'elenco. */
  della_sede?: boolean | null
  /** Comune in cui lavora (532): a chi legge l'albo. Nullo sui mediatori inseriti prima che fosse obbligatorio. */
  comune_lavoro?: string | null
  comune_lavoro_nome?: string | null
  comune_lavoro_provincia?: string | null
  /** Recapito, dove riceve (532): solo a chi gestisce l'albo. */
  recapito?: string | null
}

/** «Perugia (PG)», oppure stringa vuota se il comune in cui lavora non e' indicato. */
export const comuneLavoroLeggibile = (m: Pick<Mediatore, 'comune_lavoro_nome' | 'comune_lavoro_provincia'>) =>
  m.comune_lavoro_nome ? `${m.comune_lavoro_nome}${m.comune_lavoro_provincia ? ` (${m.comune_lavoro_provincia})` : ''}` : ''

export const STATI: Record<StatoMediazione, { etichetta: string; tono: TonoEtichetta }> = {
  programmata: { etichetta: 'in programma', tono: 'informativo' },
  svolta: { etichetta: 'svolta', tono: 'positivo' },
  annullata: { etichetta: 'annullata', tono: 'neutro' },
  non_presentata: { etichetta: 'non si è presentata', tono: 'attenzione' },
}

const comeData = (giorno: string) => new Date(`${giorno}T12:00:00Z`)

/** «martedì 21 ottobre», con l'anno solo se diverso da quello in corso. */
export function giornoLungo(giorno: string, oggi = giornoDiOggi()): string {
  const stessoAnno = giorno.slice(0, 4) === oggi.slice(0, 4)
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', ...(stessoAnno ? {} : { year: 'numeric' }),
  }).format(comeData(giorno))
}

/** Oggi a Roma, AAAA-MM-GG: lo stesso calendario dell'API. */
export function giornoDiOggi(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date())
}

/** AAAA-MM-GG spostato di `n` giorni. */
export function giornoDopo(giorno: string, n: number): string {
  return new Date(comeData(giorno).getTime() + n * 86_400_000).toISOString().slice(0, 10)
}

/** «Oggi», «Domani» oppure «Martedì 21 ottobre». */
export function giornoVicino(giorno: string): string {
  const oggi = giornoDiOggi()
  if (giorno === oggi) return 'Oggi'
  if (giorno === giornoDopo(oggi, 1)) return 'Domani'
  if (giorno === giornoDopo(oggi, -1)) return 'Ieri'
  const testo = giornoLungo(giorno, oggi)
  return testo.charAt(0).toUpperCase() + testo.slice(1)
}

/** 90 → «un’ora e mezza». */
export function durataLeggibile(minuti: number): string {
  const ore = Math.floor(minuti / 60)
  const resto = minuti % 60
  const parteOre = ore === 0 ? '' : ore === 1 ? 'un’ora' : `${ore} ore`
  if (!resto) return parteOre
  if (resto === 30 && ore > 0) return `${parteOre} e mezza`
  return ore ? `${parteOre} e ${resto} minuti` : `${resto} minuti`
}

/** Durate proposte alla programmazione. */
export const DURATE = [30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 480]
  .map((m) => ({ valore: String(m), etichetta: durataLeggibile(m) }))

/** Ore svolte: mezz'ore da 0,5 a 10, come le ore di mediazione degli interventi. */
export const ORE_SVOLTE = Array.from({ length: 20 }, (_, i) => (i + 1) / 2)
  .map((o) => ({ valore: String(o), etichetta: durataLeggibile(o * 60) }))

/** Ore svolte proposte: la durata prevista, arrotondata alla mezz'ora. */
export const oreDaDurata = (minuti: number) => String(Math.min(10, Math.max(0.5, Math.round(minuti / 30) / 2)))

/**
 * Che cosa dire dell'email al mediatore, dopo averlo avvisato nel gestionale. Vuoto se e' partita
 * o se il mediatore non usa il gestionale (allora lo dice gia' il resto del messaggio).
 */
export function fraseEmail(esito: Partial<EsitoEmail>, usaGestionale: boolean): string {
  if (!usaGestionale || esito.email_inviata) return ''
  // Il motivo tecnico resta nel registro delle email: qui basta sapere se manca la casella.
  const casella = /casella/i.test(esito.motivo_email ?? '')
  return casella
    ? 'L’email non è partita perché la casella di posta dell’ente non è ancora configurata: se serve, avvisalo a voce.'
    : 'L’email però non è partita: se serve, avvisalo a voce.'
}

/** «Sede di Perugia, Questura» per la riga «Dove». */
export function doveLeggibile(m: { sede_nome: string | null; luogo: string | null }): string {
  return [m.sede_nome, m.luogo].filter(Boolean).join(' · ') || 'da definire'
}
